import { useCallback, useState } from "react";
import { Form, Formik } from 'formik';
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Checkbox from "../../controls/form/Checkbox";
import Dropdown from "../../controls/form/Dropdown";
import { useHasPermission } from "../../../common/Hooks";
import Utils from "../../../utils/utils";
import { useFormsConfig } from "./Context";
import Deployment from "../../../services/deployment";
import GenericCheckBox from "../../controls/GenericCheckbox";
import PubSub from "../../../services/PubSub";
import Toolbar from "../../controls/Toolbar";
import TextBox from "../../controls/form/TextBox";
import * as Yup from "yup";

const SAVE_SECTIONS = {
    FORM_TEMPLATE: "formTemplate",
    SECTION: "section",
    ITEM: "item"
}

const FormPreferences = () => {
    const { id: deploymentId } = useParams();
    const [translate] = useTranslation();
    const [showSaveButton, setShowSaveButton] = useState([]);
    const { dataTypes, preferences, LABEL_FONT_SIZE_OPTIONS, COLUMNS_OPTIONS, DECIMALS_OPTIONS } = useFormsConfig();

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });

    const handleSubmit = useCallback(async (values) => {
        await Deployment.updateFormsPreferences(deploymentId, values);

        PubSub.publish("refresh-forms-preferences");
        setShowSaveButton([]);
    }, [deploymentId]);

    const handleOnChange = useCallback((saveSection) => {
        setShowSaveButton(prev => {
            if (!prev.includes(saveSection)) {
                prev.push(saveSection);
            }

            return [...prev];
        });
    }, []);

    const formTemplatePreferences = useCallback((props) =>
        <>
            <div className='col-12 mt-3 border-bottom'>
                <Toolbar
                    className="h4"
                    heading={translate("OTM_FORMS_TEMPLATE_PREFERENCES_FORM_TEMPLATE_HEADER")}
                    buttons={[
                        {
                            name: "Save",
                            type: "submit",
                            hidden: !showSaveButton.includes(SAVE_SECTIONS.FORM_TEMPLATE) || !permissions.update || !props.isValid
                        }
                    ]}
                />
            </div>
            <div className='col-6 mt-3'>
                <GenericCheckBox
                    tooltip={translate("OTM_FORM_TYPE_TOOLTIP")}
                    buttonTooltip={true}
                    initialValue={props.values.formType === 'banner'}
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_TEMPLATE_SHOW_ERROR_IN_BANNER')}
                    name='formType'
                    onChange={(value) => { props.setFieldValue('formType', props.values['formType'] === 'banner' ? 'plain' : 'banner'); handleOnChange(SAVE_SECTIONS.FORM_TEMPLATE); }}
                    disabled={!permissions.update}
                />
            </div>
            <div className='col-6 mt-3'>
                <Checkbox
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_TEMPLATE_SUBMIT_HIDDEN_FIELDS')}
                    name='submitHiddenFields'
                    disabled={!permissions.update}
                    onChange={() => { handleOnChange(SAVE_SECTIONS.FORM_TEMPLATE) }}
                />
            </div>
        </>, [handleOnChange, permissions, showSaveButton, translate]);

    const sectionPreferences = useCallback((props) => <>
        <div className='col-12 mt-3 border-bottom'>
            <Toolbar
                className="h4"
                heading={translate("OTM_FORMS_TEMPLATE_PREFERENCES_SECTION_HEADER")}
                buttons={[
                    {
                        name: "Save",
                        type: "submit",
                        hidden: !showSaveButton.includes(SAVE_SECTIONS.SECTION) || !permissions.update || !props.isValid
                    }
                ]}
            />
        </div>
        <div className='col-12 mt-3'>
            <Checkbox
                label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_SECTION_TITLE_EQUALS_NAME')}
                name='sectionTitleEqualsName'
                disabled={!permissions.update}
                onChange={() => { handleOnChange(SAVE_SECTIONS.SECTION) }}
            />
        </div>
    </>, [handleOnChange, permissions, showSaveButton, translate]);

    const itemPreferences = useCallback((props) => {
        const dataTypesOptions = dataTypes ? [{ _id: 'no-input', name: 'label-only' }, ...dataTypes.map(o => ({ _id: o, name: o }))] : [{ _id: 'no-input', name: 'label-only' }];

        return <>
            <div className='col-12 mt-3 border-bottom'>
                <Toolbar
                    className="h4"
                    heading={translate("OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_HEADER")}
                    buttons={[
                        {
                            name: "Save",
                            type: "submit",
                            hidden: !showSaveButton.includes(SAVE_SECTIONS.ITEM) || !permissions.update || !props.isValid
                        }
                    ]}
                />
            </div>
            <div className='col-6 mt-3'>
                <Dropdown
                    heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_DATA_TYPE')}
                    name='itemDataType'
                    readOnly={!permissions.update}
                    options={dataTypesOptions}
                    onSelect={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                    grow={true}
                    addEmptyOption={true}
                />
            </div>
            <div className='col-6 mt-3'>
                <TextBox
                    label={translate("OTM_MAX_LENGTH")}
                    type="number"
                    name="itemMaxLength"
                    disabled={!permissions.update}
                    onChange={(event) => { props.setFieldValue('itemMaxLength', event.target.value); handleOnChange(SAVE_SECTIONS.ITEM) }}
                />
            </div>
            <div className='col-6 mt-3'>
                <Dropdown
                    heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_ALLOWED_DECIMALS')}
                    name='itemNumberAllowedDecimals'
                    readOnly={!permissions.update}
                    options={DECIMALS_OPTIONS}
                    onSelect={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                    grow={true}
                    addEmptyOption={true}
                />
            </div>
            <div className='col-6 mt-3'>
                <Checkbox
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_SHOW_STEP_BUTTONS')}
                    name='itemNumberShowStepButtons'
                    disabled={!permissions.update}
                    onChange={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                />
            </div>
            <div className='col-12 col-sm-6 mt-3'>
                <Dropdown
                    heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_COLUMNS_SPAN_SMALL')}
                    name='itemColumnSpanSmall'
                    tooltip={translate('OTM_COLUMNS_SPAN_SMALL_TOOLTIP')}
                    buttonTooltip={true}
                    grow={true}
                    readOnly={!permissions.update}
                    onSelect={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                    options={COLUMNS_OPTIONS}
                />
            </div>
            <div className='col-12 col-sm-6 mt-3'>
                <Dropdown
                    heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_COLUMNS_SPAN_LARGE')}
                    name='itemColumnSpanLarge'
                    tooltip={translate('OTM_COLUMNS_SPAN_LARGE_TOOLTIP')}
                    buttonTooltip={true}
                    grow={true}
                    readOnly={!permissions.update}
                    onSelect={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                    options={COLUMNS_OPTIONS}
                />
            </div>
            <div className='col-12 col-sm-6 mt-3'>
                <Checkbox
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_REQUIRED')}
                    name='itemIsRequired'
                    disabled={!permissions.update}
                    onChange={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                />
            </div>
            <div className='col-12 col-sm-6 mt-3'>
                <Checkbox
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_READ_ONLY')}
                    name='itemIsReadOnly'
                    disabled={!permissions.update}
                    onChange={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                />
            </div>
            <div className='col-12 col-sm-6 mt-3'>
                <Checkbox
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_STYLES_WHEN_VALID')}
                    name='itemShowStylesWhenValid'
                    tooltip={translate('OTM_SHOW_STYLES_WHEN_VALID_TOOLTIP')}
                    buttonTooltip={true}
                    disabled={!permissions.update}
                    onChange={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                />
            </div>
            <div className='col-12 col-sm-6 mt-3'>
                <Checkbox
                    label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_STYLES_WHEN_INVALID')}
                    name='itemShowStylesWhenInvalid'
                    tooltip={translate('OTM_SHOW_STYLES_WHEN_INVALID_TOOLTIP')}
                    buttonTooltip={true}
                    disabled={!permissions.update}
                    onChange={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                />
            </div>
            <div className='col-6 mt-3'>
                <span className="d-block h6 lh-sm mb-0">
                    {translate("OTM_FORMS_TEMPLATE_PREFERENCES_LABEL_STYLES_INPUT")}
                </span>
            </div>
            <div className='col-6 mt-3'>
                <span className="d-block h6 lh-sm mb-0">
                    {translate("OTM_FORMS_TEMPLATE_PREFERENCES_LABEL_STYLES_NO_INPUT")}
                </span>
            </div>
            <div className="col-2">
                <Checkbox
                    label={translate('OTM_LABEL_BOLDED')}
                    name='itemLabelBolded'
                    disabled={!permissions.update}
                    onChange={() => {
                        handleOnChange(SAVE_SECTIONS.ITEM);
                    }}
                />
            </div>
            <div className="col-2">
                <Checkbox
                    label={translate('OTM_LABEL_ITALIC')}
                    name='itemLabelItalic'
                    disabled={!permissions.update}
                    onChange={() => {
                        handleOnChange(SAVE_SECTIONS.ITEM);
                    }}
                />
            </div>
            <div className="col-2">
                <Checkbox
                    label={translate('OTM_LABEL_UNDERLINE')}
                    name='itemLabelUnderline'
                    disabled={!permissions.update}
                    onChange={() => {
                        handleOnChange(SAVE_SECTIONS.ITEM);
                    }}
                />
            </div>
            <div className="col-2">
                <Checkbox
                    label={translate('OTM_LABEL_BOLDED')}
                    name='itemTextBolded'
                    disabled={!permissions.update}
                    onChange={() => {
                        handleOnChange(SAVE_SECTIONS.ITEM);
                    }}
                />
            </div>
            <div className="col-2">
                <Checkbox
                    label={translate('OTM_LABEL_ITALIC')}
                    name='itemTextItalic'
                    disabled={!permissions.update}
                    onChange={() => {
                        handleOnChange(SAVE_SECTIONS.ITEM);
                    }}
                />
            </div>
            <div className="col-2">
                <Checkbox
                    label={translate('OTM_LABEL_UNDERLINE')}
                    name='itemTextUnderline'
                    disabled={!permissions.update}
                    onChange={() => {
                        handleOnChange(SAVE_SECTIONS.ITEM);
                    }}
                />
            </div>
            <div className='col-6 mt-3'>
                <Dropdown
                    buttonTooltip={true}
                    tooltip={translate("OTM_LABEL_FONT_SIZE_TOOLTIP")}
                    heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_LABEL_FONT_SIZE')}
                    name="itemLabelFontSize"
                    readOnly={!permissions.update}
                    options={LABEL_FONT_SIZE_OPTIONS}
                    onSelect={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                    grow={true}
                />
            </div>
            <div className='col-6 mt-3'>
                <Dropdown
                    buttonTooltip={true}
                    tooltip={translate("OTM_LABEL_FONT_SIZE_TOOLTIP")}
                    heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_TEXT_FONT_SIZE')}
                    name="itemTextFontSize"
                    readOnly={!permissions.update}
                    options={LABEL_FONT_SIZE_OPTIONS}
                    onSelect={() => { handleOnChange(SAVE_SECTIONS.ITEM) }}
                    grow={true}
                />
            </div>
        </>
    }, [dataTypes, LABEL_FONT_SIZE_OPTIONS, COLUMNS_OPTIONS, DECIMALS_OPTIONS, handleOnChange, permissions, showSaveButton, translate]);

    return <div className="h-100 overflow-auto">
        <Formik initialValues={{
            formType: preferences?.formType ?? 'plain',
            submitHiddenFields: preferences?.submitHiddenFields ?? false,
            sectionTitleEqualsName: preferences?.sectionTitleEqualsName ?? false,
            itemDataType: preferences?.itemDataType,
            itemNumberAllowedDecimals: preferences?.itemNumberAllowedDecimals ?? 0,
            itemNumberShowStepButtons: preferences?.itemNumberShowStepButtons ?? true,
            itemLabelBolded: preferences?.itemLabelBolded ?? true,
            itemLabelItalic: preferences?.itemLabelItalic ?? false,
            itemLabelUnderline: preferences?.itemLabelUnderline ?? false,
            itemTextBolded: preferences?.itemTextBolded ?? false,
            itemTextItalic: preferences?.itemTextItalic ?? false,
            itemTextUnderline: preferences?.itemTextUnderline ?? false,
            itemLabelFontSize: preferences?.itemLabelFontSize ?? 1,
            itemTextFontSize: preferences?.itemTextFontSize ?? 1,
            itemColumnSpanSmall: preferences?.itemColumnSpanSmall ?? 12,
            itemColumnSpanLarge: preferences?.itemColumnSpanLarge ?? 12,
            itemIsRequired: preferences?.itemIsRequired ?? true,
            itemIsReadOnly: preferences?.itemIsReadOnly ?? false,
            itemShowStylesWhenValid: preferences?.itemShowStylesWhenValid ?? true,
            itemShowStylesWhenInvalid: preferences?.itemShowStylesWhenInvalid ?? true
        }}
            validationSchema={Yup.object({
                itemMaxLength: Yup.number().integer(translate("OTM_MAX_LENGTH_MUST_BE_INTEGER")).nullable()
            })}
            validateOnMount
            onSubmit={handleSubmit}
            enableReinitialize>
            {props => (
                <Form className="container-fluid">
                    <div className="card">
                        <div className="card-body row mt-n3 align-items-start">
                            {formTemplatePreferences(props)}

                            {sectionPreferences(props)}

                            {itemPreferences(props)}

                        </div>
                    </div>
                </Form>)}
        </Formik >
    </div>;
}

export default FormPreferences;